import React from 'react';
import Container from '@cloudscape-design/components/container';
import Box from '@cloudscape-design/components/box';
import Header from '@cloudscape-design/components/header';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Select from '@cloudscape-design/components/select';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Alert from '@cloudscape-design/components/alert';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import Table from '@cloudscape-design/components/table';
import BarChart from '@cloudscape-design/components/bar-chart';
import Tiles from '@cloudscape-design/components/tiles';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Popover from '@cloudscape-design/components/popover';
import Link from '@cloudscape-design/components/link';

import {
  runIgJob,
  fetchIgResults,
  fetchIgManifest,
  fetchIgAsinResults,
  IgJobRequest,
  IgAttributionRow,
  IgManifest,
  IgManifestAsin,
  submitIgJob,
  getIgJobStatus,
  IgSubmitResponse,
} from '../api/IgApi';
import AttributionHeatmap from './AttributionHeatmap';
import AgentExplanationInline from './AgentExplanationInline';
import AgentChat from './AgentChat';
import RecentRunsApiPanel from './RecentRunsApiPanel';
import { businessesForView, regionsForBusiness } from '../config/businesses';
import { toCsv } from '../utils/csv';
import { ResultRow } from '../api/ForecastApi';
import { todayString } from '../utils/date';
import { saveRecentRun, RecentRunItem } from '../api/recentRunsApi';

type Display = 'chart' | 'table';
type Method = 'ig' | 'perturbation';
type PerturbationMode = 'group' | 'feature';
type Baseline = 'curated' | 'zero';
type Option = { label: string; value: string };

const toOptions = (values: string[]): Option[] =>
  values.map((v) => ({ label: v, value: v }));

// Parse a perturbation lead-time spec (single int "7", comma list "0,7,14",
// or inclusive range "0-91", possibly mixed) into a sorted, de-duplicated list
// of ints. Returns null on malformed input.
function parseLeadtimesSpec(spec: string): number[] | null {
  const trimmed = spec.trim();
  if (!trimmed) return null;
  const values = new Set<number>();
  for (const rawTok of trimmed.split(',')) {
    const tok = rawTok.trim();
    if (!tok) continue;
    if (tok.includes('-')) {
      const [loStr, hiStr] = tok.split('-');
      const lo = Number(loStr.trim());
      const hi = Number(hiStr.trim());
      if (!Number.isInteger(lo) || !Number.isInteger(hi) || lo > hi) return null;
      for (let v = lo; v <= hi; v += 1) values.add(v);
    } else {
      const v = Number(tok);
      if (!Number.isInteger(v)) return null;
      values.add(v);
    }
  }
  if (values.size === 0) return null;
  return [...values].sort((a, b) => a - b);
}

// Validate a perturbation lead-time spec: must parse and every resolved lead
// time must be an integer in [0, 91]. Returns an error string or null.
function validateLeadtimesSpec(spec: string): string | null {
  const parsed = parseLeadtimesSpec(spec);
  if (!parsed) {
    return "Lead time(s) must be an int, comma list, or range (e.g. '0-91')";
  }
  const outOfRange = parsed.filter((v) => v < 0 || v > 91);
  if (outOfRange.length > 0) {
    return `Lead time(s) must be between 0 and 91: ${outOfRange.join(', ')}`;
  }
  return null;
}

const METHOD_OPTIONS = [
  {
    value: 'ig',
    label: 'Integrated Gradients',
    description: 'Axiom-respecting attributions via path integration (slower, exact)',
  },
  {
    value: 'perturbation',
    label: 'Perturbation',
    description: 'Feature ablation with curated baselines (faster, approximate)',
  },
];

const PERTURBATION_MODE_OPTIONS: Option[] = [
  { label: 'Group — occlude feature groups', value: 'group' },
  { label: 'Feature — per-column attribution', value: 'feature' },
];

const BASELINE_OPTIONS: Option[] = [
  { label: 'Curated (domain-expert defaults)', value: 'curated' },
  { label: 'Zero (all features to 0)', value: 'zero' },
];

// Non-feature reconstruction diagnostics emitted by run_ig.py. For these rows the
// p50_score/p90_score columns hold FORECAST values (not attribution scores), so
// they're shown in a dedicated summary panel rather than the feature table.
const SUMMARY_LABELS = ['BASELINE', 'RECONSTRUCTED', 'ACTUAL FORECAST'];
const isSummaryRow = (feature: string): boolean =>
  SUMMARY_LABELS.includes(feature) || feature.startsWith('GAP');

interface SummaryItem {
  label: string;
  meaning: string;
  p50: string;
  p90: string;
}

// Plain-language meaning of each reconstruction diagnostic row.
const summaryMeaning = (feature: string): string => {
  if (feature === 'BASELINE')
    return 'Forecast when every input is set to the baseline (the "no-signal" reference).';
  if (feature === 'ACTUAL FORECAST')
    return "The model's real forecast for this ASIN.";
  if (feature === 'RECONSTRUCTED')
    return 'BASELINE + Σ(all attributions) — should ≈ ACTUAL if IG converged.';
  if (feature.startsWith('GAP'))
    return 'RECONSTRUCTED − ACTUAL = the convergence error from finite IG steps.';
  return '';
};

// One-line teaser shown in the title info popover.
const HOW_TO_READ_TEASER =
  'Shows how much each input pushed this forecast up (red) or down (blue), by feature and day.';

// Full "how to read this" guide for Integrated Gradients (plain-language).
function HowToReadIg(): JSX.Element {
  return (
    <SpaceBetween size="m">
      <Box variant="p">
        This view explains a single forecast (one ASIN, forecast-creation date, and
        lead time). It uses Integrated Gradients to estimate how much each input
        pushed the forecast up or down relative to a neutral “baseline” scenario,
        broken down by feature and by day.
      </Box>

      <div>
        <Box variant="h5">Baseline (what “neutral” means)</Box>
        <Box variant="p" color="text-body-secondary">
          Contributions are measured against a neutral reference scenario, not
          against zero. In that reference: promotions and deals are turned{' '}
          <b>off</b>, holiday indicators are <b>off</b>, demand is set to the
          item's <b>average</b>, and inventory is treated as{' '}
          <b>fully in‑stock</b>. Holiday <i>distances</i> (days‑to‑holiday),
          calendar, and cross‑ASIN average demand are kept at their{' '}
          <b>real values</b>, and item‑age features are set to a{' '}
          <b>fixed date reference</b>. A feature kept at its real value has
          nothing to compare against, so it won't show up as a contribution
          (e.g. a Christmas effect carried by “days‑to‑Christmas” can stay hidden
          even near the holiday). Read every attribution as “effect versus this
          no‑promo, average‑demand, fully‑stocked reference.”
        </Box>
      </div>

      <div>
        <Box variant="h5">Colors</Box>
        <ul>
          <li>Red = the input pushed the forecast up.</li>
          <li>Blue = it pushed the forecast down.</li>
          <li>Darker = a stronger effect; near-white = little or no effect.</li>
        </ul>
      </div>

      <div>
        <Box variant="h5">Rows and time</Box>
        <Box variant="p">
          Each row is a feature; cells run left-to-right over time (day by day).
          All contributions fall before the forecast-creation date (FCD): the
          forecast is made at the FCD, so days after it can’t influence it. When a
          future region is present, a dashed FCD line marks the boundary.
        </Box>
      </div>

      <div>
        <Box variant="h5">Which days and features appear</Box>
        <Box variant="p">
          For each feature we show its most influential days — up to the top 20 by
          impact, chosen separately for P50 and P90. Days and features that
          contribute essentially nothing are hidden, so the view stays focused on
          what actually moved the forecast. Use the P50 / P90 toggle to switch which
          forecast you’re explaining.
        </Box>
      </div>

      <div>
        <Box variant="h5">Table columns</Box>
        <ul>
          <li>
            <strong>P50 Score / P90 Score</strong> — that day’s contribution to the
            P50 / P90 forecast.
          </li>
          <li>
            <strong>P50 Total Attribution / P90 Total Attribution</strong> — the
            feature’s contribution summed across all days (its overall importance).
          </li>
          <li>
            <strong>P50 Rank / P90 Rank</strong> — where that day ranks among the
            feature’s strongest days (1 = strongest). A blank means the day isn’t in
            that forecast’s top 20.
          </li>
          <li>
            <strong>Actual / Baseline</strong> — the feature’s real value that day vs.
            its baseline value.
          </li>
        </ul>
      </div>

      <div>
        <Box variant="h5">The summary rows (Forecast reconstruction)</Box>
        <ul>
          <li>
            <strong>Baseline</strong> — the forecast if every input were at its
            baseline.
          </li>
          <li>
            <strong>Actual Forecast</strong> — the model’s real forecast.
          </li>
          <li>
            <strong>Reconstructed</strong> — Baseline + all contributions. It should
            closely match Actual.
          </li>
          <li>
            <strong>Gap</strong> — how far Reconstructed is from Actual. A small gap
            means the contributions add up faithfully; a large gap means interpret
            the attributions with caution.
          </li>
        </ul>
      </div>

      <div>
        <Box variant="h5">Color-scale toggle</Box>
        <ul>
          <li>
            <strong>Shared scale</strong> (default) — one color scale for the whole
            grid, so intensities are comparable across features. If one feature
            dominates, smaller ones may look faint.
          </li>
          <li>
            <strong>Per-feature</strong> — each row is scaled to its own peak (shown
            as ±peak next to the feature name), so smaller features reveal their own
            patterns. In this mode color intensity is not comparable between rows.
          </li>
        </ul>
      </div>

      <ExpandableSection headerText="Methodology / advanced" variant="footer">
        <SpaceBetween size="s">
          <Box variant="p" color="text-body-secondary">
            <b>Method.</b> Integrated Gradients (path integration from baseline →
            actual), computed separately for the P50 and P90 forecast at the
            chosen lead time/span. Completeness:{' '}
            <code>baseline + Σ(attributions) ≈ actual</code>.
          </Box>

          <Box variant="p" color="text-body-secondary">
            <b>Baseline recipe (per feature):</b>
          </Box>
          <Box variant="p" color="text-body-secondary">
            <ul>
              <li><code>age_*</code> → days since 2020‑01‑01</li>
              <li><code>causal_*</code>, <code>deal_*</code> (promotions/deals) → 0</li>
              <li><code>holiday_indicator_*</code>, <code>holiday_reduced_operating_hour_indicator</code> → 0</li>
              <li><code>holiday_distance_*</code>, calendar (month, day‑of‑week, day‑beginning) → actual</li>
              <li>cross‑ASIN demand (<code>asin_avg_demand</code>, <code>fg_avg_demand</code>, <code>marketplace_asin_avg_demand</code>) → actual</li>
              <li><code>demand</code> → asin average for history, −1 after FCD</li>
              <li><code>inventory</code> → 24 (fully in‑stock) for history, −1 after FCD; <code>inventory_denominator</code> → actual</li>
              <li>static features → actual</li>
            </ul>
          </Box>

          <Box variant="p" color="text-body-secondary">
            <b>What's shown / hidden.</b> A feature — time‑series or static — is
            dropped if its total attribution is below <b>0.01</b> in both P50 and
            P90. Within a shown time‑series feature, only days with |attribution| ≥{' '}
            <b>0.0001</b> are ranked, keeping the <b>top 20 days per quantile</b>
            (P50 and P90 chosen independently, then unioned). A blank rank = not in
            that quantile's top 20.
          </Box>

          <Box variant="p" color="text-body-secondary">
            <b>IG Steps &amp; the Gap.</b> <code>IG Steps</code> (default 200, range
            20–1000) sets the integration resolution. The <b>Gap</b> row is{' '}
            <code>Reconstructed − Actual</code>: small = faithful; a large Gap means
            raise IG Steps and/or interpret with caution (some model components can
            leave a residual gap regardless of steps).
          </Box>

          <Box variant="p" color="text-body-secondary">
            <b>Interpretation is baseline‑relative.</b> All values answer “effect
            vs. the reference above.” A different baseline (e.g. one that also
            removes holiday <i>distance</i>) would surface different drivers —
            expected, not a contradiction.
          </Box>
        </SpaceBetween>
      </ExpandableSection>
    </SpaceBetween>
  );
}

// "How to read this" guide for the Perturbation method. Unlike IG, perturbation
// measures each feature's importance by occluding it (replacing it with its
// baseline value) and observing how much the forecast moves — one score per
// feature per lead time, with no per-day gradient breakdown.
function HowToReadPerturbation(): JSX.Element {
  return (
    <SpaceBetween size="m">
      <Box variant="p">
        This view explains a forecast by <b>perturbation</b>: for each input
        feature, the model is re-run with that feature replaced by a neutral
        “baseline” value, and the resulting change in the forecast is that
        feature’s attribution. A larger swing means the feature mattered more.
        Scores are produced per feature and per selected lead time.
      </Box>

      <div>
        <Box variant="h5">Baseline (what “neutral” means)</Box>
        <Box variant="p" color="text-body-secondary">
          Contributions are measured against a neutral reference, not against
          zero. With the <b>curated</b> baseline: promotions/deals and holiday
          indicators are turned <b>off</b>, demand is set to the item’s{' '}
          <b>average</b> (−1 after the FCD), and inventory is treated as{' '}
          <b>fully in‑stock</b> (−1 after the FCD). Holiday <i>distances</i>,
          calendar, and cross‑ASIN average demand are kept at their{' '}
          <b>real values</b>, and item‑age features use a <b>fixed date
          reference</b>. A feature kept at its real value has nothing to compare
          against, so it won’t show up as a contribution. The <b>zero</b>{' '}
          baseline instead sets every occluded feature to 0.
        </Box>
      </div>

      <div>
        <Box variant="h5">Group vs. Feature mode</Box>
        <ul>
          <li>
            <strong>Feature</strong> — occludes one column at a time; finest
            granularity (one score per individual feature).
          </li>
          <li>
            <strong>Group</strong> — occludes a whole feature group at once
            (e.g. all promo signals together); fewer, coarser scores.
          </li>
        </ul>
      </div>

      <div>
        <Box variant="h5">Lead times</Box>
        <Box variant="p">
          You can request a single lead time, a list, or a range (e.g.{' '}
          <code>0-91</code>). When more than one lead time is present, use the{' '}
          <b>lead-time selector</b> above the results to switch which one the
          table and chart show. Ranking is per lead time (rank 1 = the strongest
          feature for that lead time).
        </Box>
      </div>

      <div>
        <Box variant="h5">Table columns</Box>
        <ul>
          <li>
            <strong>P50 Score / P90 Score</strong> — the feature’s contribution
            to the P50 / P90 forecast at that lead time.
          </li>
          <li>
            <strong>Rank</strong> — the feature’s rank by |P50 score| within the
            selected lead time (1 = strongest).
          </li>
        </ul>
      </div>

      <div>
        <Box variant="h5">Interpreting the scores</Box>
        <Box variant="p" color="text-body-secondary">
          Perturbation does <b>not</b> satisfy an exact completeness axiom the
          way Integrated Gradients does — feature interactions mean individual
          occlusions don’t perfectly sum to the total forecast gap. Scores are
          normalized to sum to the gap, so read them as a directional,
          approximate ranking of drivers rather than an exact decomposition.
        </Box>
      </div>
    </SpaceBetween>
  );
}

export interface ExplainabilityPrefill {
  business?: string;
  region?: string;
  fg?: string;
  fcd?: string;
  asins?: string; // comma-separated; first is used today, full list once multi-ASIN lands
}

export interface ExplainabilityViewProps {
  prefill?: ExplainabilityPrefill;
}

export default function ExplainabilityView({ prefill }: ExplainabilityViewProps = {}): JSX.Element {
  // Common fields
  const [method, setMethod] = React.useState<Method>('ig');
  const [business, setBusiness] = React.useState<Option | null>(null);
  const [region, setRegion] = React.useState<Option | null>(null);
  const [asin, setAsin] = React.useState('');
  const [fg, setFg] = React.useState('');
  const [fcd, setFcd] = React.useState('');
  const [leadtime, setLeadtime] = React.useState('7');
  const [span, setSpan] = React.useState('1');

  // Perturbation multi-lead-time spec (string): single int, comma list, or
  // inclusive range (e.g. "0-91"). Sent as `leadtimes` for perturbation; the
  // IG path keeps using the single numeric `leadtime` above.
  const [pertLeadtimes, setPertLeadtimes] = React.useState('0-91');
  // Apply prefill coming from the divergence view's "Investigate this FCD". Pre-fills the
  // shared fields without submitting so the user can override (e.g. edit ASIN) then Analyze.
  const prefillKey = prefill
    ? [prefill.business, prefill.region, prefill.fg, prefill.fcd, prefill.asins].join('|')
    : '';
  React.useEffect(() => {
    if (!prefill) return;
    if (prefill.business) setBusiness({ label: prefill.business, value: prefill.business });
    if (prefill.region) setRegion({ label: prefill.region, value: prefill.region });
    if (prefill.fg) setFg(prefill.fg);
    if (prefill.fcd) setFcd(prefill.fcd);
    // ASIN field accepts a comma-separated list (multi-ASIN supported), so pass it through.
    if (prefill.asins) setAsin(prefill.asins);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillKey]);

  // IG-specific
  const [nSteps, setNSteps] = React.useState('200');

  // Perturbation-specific
  const [pertMode, setPertMode] = React.useState<Option>(PERTURBATION_MODE_OPTIONS[1]);
  const [baseline, setBaseline] = React.useState<Option>(BASELINE_OPTIONS[0]);
  const [quantiles, setQuantiles] = React.useState('0.5,0.9');
  const [groups, setGroups] = React.useState('');
  const [batchSize, setBatchSize] = React.useState('4');

  // State
  const [loading, setLoading] = React.useState(false);
  const [jobStatus, setJobStatus] = React.useState<string | null>(null);
  // Which method the current job/results belong to, so a job submitted under IG is not
  // shown while the Perturbation tab is active (and vice versa).
  const [jobMethod, setJobMethod] = React.useState<Method | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [rows, setRows] = React.useState<IgAttributionRow[] | null>(null);
  const [pertResponse, setPertResponse] = React.useState<IgSubmitResponse | null>(null);
  const [display, setDisplay] = React.useState<Display>('chart');
  const [heatmapMetric, setHeatmapMetric] = React.useState<'p50_score' | 'p90_score'>(
    'p50_score'
  );

  // Perturbation multi-lead-time selector. When a perturbation result contains
  // more than one distinct lead_time, a Select lets the user pick which lead
  // time's rows feed the table + bar chart. null => no selection yet / N/A.
  const [selectedLeadTime, setSelectedLeadTime] = React.useState<Option | null>(null);

  // Multi-ASIN IG state
  const [manifest, setManifest] = React.useState<IgManifest | null>(null);
  const [selectedAsin, setSelectedAsin] = React.useState<Option | null>(null);
  const [loadingAsin, setLoadingAsin] = React.useState(false);

  // Recent runs refresh counter — incrementing triggers RecentRunsPanel re-render
  const [recentRunsRefresh, setRecentRunsRefresh] = React.useState(0);

  const abortRef = React.useRef<AbortController | null>(null);
  React.useEffect(() => () => abortRef.current?.abort(), []);

  const regionOptions = business ? toOptions(regionsForBusiness(business.value)) : [];

  const validate = (): string | null => {
    if (!business) return 'Business is required';
    if (!region) return 'Region is required';
    const asins = asin.split(',').map((a) => a.trim()).filter(Boolean);
    if (asins.length === 0) return 'At least one ASIN is required';
    if (asins.length > 100) return 'Maximum 100 ASINs';
    for (const a of asins) {
      if (!/^[A-Z0-9]{10}$/.test(a)) return `Invalid ASIN: ${a}`;
    }
    if (!fg || isNaN(Number(fg))) return 'FG must be a number';
    if (!fcd || !/^\d{4}-\d{2}-\d{2}$/.test(fcd)) return 'FCD must be YYYY-MM-DD';
    if (method === 'perturbation') {
      const ltError = validateLeadtimesSpec(pertLeadtimes);
      if (ltError) return ltError;
    } else if (!leadtime || isNaN(Number(leadtime))) {
      return 'Leadtime must be a number';
    }
    if (!span || isNaN(Number(span))) return 'Span must be a number';

    return null;
  };

  const onSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    setRows(null);
    setManifest(null);
    setSelectedAsin(null);
    setPertResponse(null);
    setJobStatus('submitting');
    setJobMethod(method);

    try {
      if (method === 'ig') {
        const request: IgJobRequest = {
          business: business!.value,
          region: region!.value,
          asin,
          fg: Number(fg),
          fcd,
          leadtime: Number(leadtime),
          span: Number(span),
          nSteps: Number(nSteps),
        };

        const { outputS3 } = await runIgJob(
          request,
          (status) => setJobStatus(status),
          controller.signal
        );

        setJobStatus('loading results');
        const manifestData = await fetchIgManifest(outputS3, controller.signal);
        setManifest(manifestData);

        // Auto-select first succeeded ASIN and load its results
        const firstSucceeded = manifestData.asins.find((a) => a.status === 'succeeded');
        if (firstSucceeded?.output_s3) {
          setSelectedAsin({ label: firstSucceeded.asin, value: firstSucceeded.asin });
          const results = await fetchIgAsinResults(firstSucceeded.output_s3, controller.signal);
          setRows(results);
        }

        setJobStatus(null);
        saveRecentRun({
          view_type: 'ig',
          label: [asin, `fg:${fg}`, fcd, business?.value, region?.value].filter(Boolean).join(' \u00b7 '),
          params: { asin, fg, fcd, business: business?.value || '', region: region?.value || '', leadtime, span, nSteps, method: 'ig' },
          handle: { outputS3 },
        }).catch(() => {});
        setRecentRunsRefresh((c) => c + 1);
      } else {
        // Perturbation method
        const request: any = {
          business: business!.value,
          region: region!.value,
          asin,
          fg: Number(fg),
          fcd,
          leadtime: Number(leadtime),
          span: Number(span),
          method: 'perturbation',
          mode: pertMode.value,
          baseline: baseline.value,
          quantiles,
          batchSize: Number(batchSize),
        };

        // Multi-lead-time: send the range/list string as `leadtimes`. The
        // backend prefers it over the single `leadtime` and stacks one row per
        // (feature x lead_time x quantile) in the CSV.
        if (pertLeadtimes.trim()) {
          request.leadtimes = pertLeadtimes.trim();
        }

        if (pertMode.value === 'group' && groups.trim()) {
          request.groups = groups.trim();
        }

        const submitResponse = await submitIgJob(request, controller.signal);
        setPertResponse(submitResponse);
        setJobStatus('submitted — polling');

        // Poll until complete
        const POLL_INTERVAL = 5000;
        const POLL_TIMEOUT = 30 * 60 * 1000;
        const deadline = Date.now() + POLL_TIMEOUT;

        while (Date.now() < deadline) {
          if (controller.signal.aborted) throw new DOMException('Cancelled', 'AbortError');
          await new Promise((r) => setTimeout(r, POLL_INTERVAL));

          const status = await getIgJobStatus(submitResponse.jobId, controller.signal);
          setJobStatus(status.status);

          if (status.status === 'succeeded') {
            setJobStatus('loading results');
            // Multi-ASIN: the perturbation explainer now writes a manifest.json
            // + per_asin/{asin}/ig_attribution_table.csv under outputS3, exactly
            // like IG. fetchIgManifest / fetchIgAsinResults are method-agnostic
            // (they hit /ig/results), so reuse them. Fall back to the legacy
            // single-CSV layout when no manifest is present.
            try {
              const manifestData = await fetchIgManifest(
                submitResponse.outputS3,
                controller.signal
              );
              setManifest(manifestData);
              const firstSucceeded = manifestData.asins.find((a) => a.status === 'succeeded');
              if (firstSucceeded?.output_s3) {
                setSelectedAsin({ label: firstSucceeded.asin, value: firstSucceeded.asin });
                const results = await fetchIgAsinResults(
                  firstSucceeded.output_s3,
                  controller.signal
                );
                setRows(results);
              }
            } catch {
              // No manifest — legacy single-ASIN output, fetch CSV directly.
              const results = await fetchIgResults(submitResponse.outputS3, controller.signal);
              setRows(results);
            }
            setJobStatus(null);
            saveRecentRun({
              view_type: 'perturbation',
              label: [asin, `fg:${fg}`, fcd, business?.value, region?.value].filter(Boolean).join(' \u00b7 '),
              params: { asin, fg, fcd, business: business?.value || '', region: region?.value || '', leadtime, span, method: 'perturbation' },
              handle: { outputS3: submitResponse.outputS3 },
            }).catch(() => {});
            setRecentRunsRefresh((c) => c + 1);
            break;
          }
          if (status.status === 'failed') {
            throw new Error(status.error || 'Perturbation job failed');
          }
        }
      }
    } catch (e) {
      if (controller.signal.aborted) return;
      setError(e instanceof Error ? e.message : 'Job failed');
      setJobStatus(null);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  const onCancel = () => {
    abortRef.current?.abort();
    setLoading(false);
    setJobStatus(null);
  };

  const handleLoadRun = async (run: RecentRunItem) => {
    const p = (run.params ?? {}) as Record<string, string>;
    const h = (run.handle ?? {}) as Record<string, string>;
    const outputS3 = h.outputS3 as string;
    setLoading(true);
    setError(null);
    setRows(null);
    setManifest(null);
    setSelectedAsin(null);
    setJobStatus('loading cached results');
    try {
      // Try manifest first (multi-ASIN v5 output)
      try {
        const manifestData = await fetchIgManifest(outputS3);
        setManifest(manifestData);
        const firstSucceeded = manifestData.asins.find((a) => a.status === 'succeeded');
        if (firstSucceeded?.output_s3) {
          setSelectedAsin({ label: firstSucceeded.asin, value: firstSucceeded.asin });
          const results = await fetchIgAsinResults(firstSucceeded.output_s3);
          setRows(results);
        }
      } catch {
        // No manifest — legacy single-ASIN output, fetch CSV directly
        const results = await fetchIgResults(outputS3);
        setRows(results);
      }
      // Pre-fill form fields from the run
      if (p.method) setMethod(p.method as Method);
      setAsin(p.asin ?? '');
      setFg(p.fg ?? '');
      setFcd(p.fcd ?? '');
      setLeadtime(p.leadtime ?? '');
      setSpan(p.span ?? '');
      if (p.nSteps) setNSteps(p.nSteps);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load cached results');
    } finally {
      setLoading(false);
      setJobStatus(null);
    }
  };

  // Export the full attribution table (all rows, mirroring ig_attribution_table.csv).
  const downloadCsv = () => {
    if (!rows || rows.length === 0) return;
    const csv = toCsv(rows as unknown as ResultRow[]);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `freshsight_ig_${selectedAsin?.value ?? asin}_fg${fg}_lt${leadtime}_${todayString()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Distinct lead times present in the loaded rows. Perturbation multi-lead-time
  // CSVs stack one row per (feature x lead_time x quantile) and carry a
  // `lead_time` column; IG CSVs don't, so this stays empty for IG (single-lead
  // -time perturbation yields one entry). Sorted ascending.
  const leadTimes = React.useMemo<number[]>(() => {
    if (method !== 'perturbation' || !rows) return [];
    const set = new Set<number>();
    for (const r of rows) {
      if (typeof r.lead_time === 'number' && Number.isFinite(r.lead_time)) {
        set.add(r.lead_time);
      }
    }
    return [...set].sort((a, b) => a - b);
  }, [rows, method]);

  // The lead-time selector only makes sense when there is more than one.
  const showLeadTimeSelector = leadTimes.length > 1;

  // Default-select the first (smallest) lead time whenever the set of lead
  // times changes (e.g. a new perturbation result loads).
  React.useEffect(() => {
    if (leadTimes.length > 0) {
      const first = String(leadTimes[0]);
      setSelectedLeadTime((prev) =>
        prev && leadTimes.some((lt) => String(lt) === prev.value)
          ? prev
          : { label: first, value: first }
      );
    } else {
      setSelectedLeadTime(null);
    }
  }, [leadTimes]);

  // Rows feeding the table + chart, filtered to the selected lead time when a
  // multi-lead-time perturbation result is showing. When there's a single (or
  // no) lead_time column (all IG results, single-lead-time perturbation), pass
  // rows through unchanged so the IG heatmap path is entirely UNAFFECTED.
  const displayRows = React.useMemo<IgAttributionRow[]>(() => {
    if (!rows) return [];
    if (!showLeadTimeSelector || !selectedLeadTime) return rows;
    const target = Number(selectedLeadTime.value);
    return rows.filter(
      (r) => typeof r.lead_time !== 'number' || r.lead_time === target
    );
  }, [rows, showLeadTimeSelector, selectedLeadTime]);

  // Split reconstruction diagnostics (BASELINE/RECONSTRUCTED/ACTUAL FORECAST/GAP)
  // out of the feature table into a dedicated summary panel; the feature table
  // then shows only real features (consistent with the heatmap).
  const summaryItems = React.useMemo<SummaryItem[]>(
    () =>
      displayRows
        .filter((r) => isSummaryRow(r.feature))
        .map((r) => ({
          label: String(r.feature),
          meaning: summaryMeaning(String(r.feature)),
          p50:
            r.p50_score != null && String(r.p50_score) !== ''
              ? Number(r.p50_score).toFixed(2)
              : '',
          p90:
            r.p90_score != null && String(r.p90_score) !== ''
              ? Number(r.p90_score).toFixed(2)
              : '',
        })),
    [displayRows]
  );
  const featureTableRows = React.useMemo(
    () => displayRows.filter((r) => !isSummaryRow(r.feature)),
    [displayRows]
  );

  // Perturbation produces exactly one score per feature (no per-day
  // breakdown), so its day_offset is always 'N/A' -- feeding that into the
  // per-day AttributionHeatmap would misclassify every feature as "static"
  // (Number('N/A') is NaN) and collapse everything into a single narrow
  // column. Use a feature-level bar chart for perturbation instead.
  const featureTotals = React.useMemo(() => {
    const map = new Map<string, { p50: number; p90: number }>();
    for (const r of featureTableRows) {
      const name = r.feature.replace(/^static:/, '');
      const existing = map.get(name);
      if (!existing) {
        map.set(name, {
          p50: typeof r.p50_total === 'number' ? r.p50_total : Number(r.p50_score),
          p90: typeof r.p90_total === 'number' ? r.p90_total : Number(r.p90_score),
        });
      }
    }
    return [...map.entries()]
      .sort((a, b) => Math.abs(b[1].p50) - Math.abs(a[1].p50))
      .slice(0, 20);
  }, [featureTableRows]);

  const chartData = React.useMemo(
    () => featureTotals.map(([name, vals]) => ({ x: name, y: vals.p50 })),
    [featureTotals]
  );

  const p90ChartData = React.useMemo(
    () => featureTotals.map(([name, vals]) => ({ x: name, y: vals.p90 })),
    [featureTotals]
  );

  // Only surface job/results that belong to the currently selected method, so an IG job
  // doesn't appear under the Perturbation tab (they are separate jobs).
  const methodActive = jobMethod === null || jobMethod === method;
  const vLoading = loading && methodActive;

  return (
    <SpaceBetween size="l">
      {/* Method selector */}
      <Container header={<Header variant="h2">Explainability Method</Header>}>
        <Tiles
          value={method}
          onChange={({ detail }) => {
            setMethod(detail.value as Method);
            setRows(null);
            setManifest(null);
            setSelectedAsin(null);
            setError(null);
            setPertResponse(null);
          }}
          items={METHOD_OPTIONS}
        />
      </Container>

      {/* Parameters form */}
      <Container
        header={
          <Header variant="h2">
            {method === 'ig' ? 'Integrated Gradients' : 'Perturbation'} — Parameters
          </Header>
        }
      >
        <form onSubmit={(e) => e.preventDefault()}>
          <Form>
            <SpaceBetween size="l">
              {/* Common fields */}
              <ColumnLayout columns={5} minColumnWidth={160}>
                <FormField label="Business" description="Business line">
                  <Select
                    selectedOption={business}
                    onChange={({ detail }) => {
                      setBusiness(detail.selectedOption as Option);
                      setRegion(null);
                    }}
                    options={toOptions(businessesForView('explainability'))}
                    placeholder="Select a business"
                  />
                </FormField>
                <FormField label="Region" description="Marketplace region">
                  <Select
                    selectedOption={region}
                    onChange={({ detail }) => setRegion(detail.selectedOption as Option)}
                    options={regionOptions}
                    placeholder="Select a region"
                    disabled={!business}
                  />
                </FormField>
                <FormField label="ASINs" description="Comma-separated, up to 100">
                  <Input
                    value={asin}
                    onChange={({ detail }) => setAsin(detail.value.toUpperCase())}
                    placeholder="e.g. B01MZHDHVM,B0014GLSE6"
                  />
                </FormField>
                <FormField label="FG" description="Forecast group ID">
                  <Input
                    value={fg}
                    onChange={({ detail }) => setFg(detail.value)}
                    placeholder="e.g. 310617"
                  />
                </FormField>
                <FormField label="FCD (Forecast Date)" description="Forecast creation date">
                  <Input
                    value={fcd}
                    onChange={({ detail }) => setFcd(detail.value)}
                    placeholder="YYYY-MM-DD"
                  />
                </FormField>
              </ColumnLayout>

              <ColumnLayout columns={3} minColumnWidth={140}>
                {method === 'ig' ? (
                  <FormField label="Lead time (days)" description="1–91">
                    <Input
                      value={leadtime}
                      onChange={({ detail }) => setLeadtime(detail.value)}
                      type="number"
                    />
                  </FormField>
                ) : (
                  <FormField
                    label="Lead time(s)"
                    description="Single, list, or range: e.g. 7, 0,7,14 or 0-91"
                  >
                    <Input
                      value={pertLeadtimes}
                      onChange={({ detail }) => setPertLeadtimes(detail.value)}
                      placeholder="0-91"
                    />
                  </FormField>
                )}
                <FormField label="Span (days)" description="1–7">
                  <Input
                    value={span}
                    onChange={({ detail }) => setSpan(detail.value)}
                    type="number"
                  />
                </FormField>
                {method === 'ig' ? (
                  <FormField label="IG Steps" description="20–1000, default 200">
                    <Input
                      value={nSteps}
                      onChange={({ detail }) => setNSteps(detail.value)}
                      type="number"
                      placeholder="20–1000, default 200"
                    />
                  </FormField>
                ) : (
                  <FormField label="Quantiles" description="Comma-separated, e.g. 0.5,0.9">
                    <Input
                      value={quantiles}
                      onChange={({ detail }) => setQuantiles(detail.value)}
                      placeholder="0.5,0.9"
                    />
                  </FormField>
                )}
              </ColumnLayout>

              {/* Perturbation-specific fields */}
              {method === 'perturbation' && (
                <>
                  <ColumnLayout columns={3} minColumnWidth={200}>
                    <FormField label="Perturbation Mode">
                      <Select
                        selectedOption={pertMode}
                        onChange={({ detail }) =>
                          setPertMode(detail.selectedOption as Option)
                        }
                        options={PERTURBATION_MODE_OPTIONS}
                      />
                    </FormField>
                    <FormField label="Baseline">
                      <Select
                        selectedOption={baseline}
                        onChange={({ detail }) =>
                          setBaseline(detail.selectedOption as Option)
                        }
                        options={BASELINE_OPTIONS}
                      />
                    </FormField>
                    <FormField label="Batch Size">
                      <Input
                        value={batchSize}
                        onChange={({ detail }) => setBatchSize(detail.value)}
                        type="number"
                      />
                    </FormField>
                  </ColumnLayout>

                  {pertMode.value === 'group' && (
                    <FormField
                      label="Feature Groups (optional)"
                      description="Comma-separated subset: promos, holidays, calendar, inventory, product_age, static_attrs, demand"
                    >
                      <Input
                        value={groups}
                        onChange={({ detail }) => setGroups(detail.value)}
                        placeholder="Leave empty for all groups"
                      />
                    </FormField>
                  )}
                </>
              )}

              {/* Submit */}
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="primary" onClick={onSubmit} loading={vLoading}>
                  {method === 'ig' ? 'Run IG' : 'Run Perturbation'}
                </Button>
                {loading && <Button onClick={onCancel}>Cancel</Button>}
                {jobStatus && jobMethod === method && (
                  <StatusIndicator type="loading">{jobStatus}</StatusIndicator>
                )}
              </SpaceBetween>
            </SpaceBetween>
          </Form>
        </form>
      </Container>

      {/* Recent Runs */}
      <RecentRunsApiPanel viewType={method === 'perturbation' ? 'perturbation' : 'ig'} onLoadRun={handleLoadRun} refreshKey={recentRunsRefresh} />

      {/* Error display */}
      {error && (
        <Alert type="error" header="Error">
          {error}
        </Alert>
      )}

      {/* Multi-ASIN selector (IG only) */}
      {methodActive && manifest && manifest.succeeded > 1 && (
        <Container header={<Header variant="h2">
          {manifest.succeeded}/{manifest.total_asins} ASINs completed · {manifest.wall_time_s.toFixed(0)}s
        </Header>}>
          <SpaceBetween size="m">
            <FormField label="Select ASIN to view">
              <Select
                selectedOption={selectedAsin}
                onChange={({ detail }) => {
                  const opt = detail.selectedOption as Option;
                  setSelectedAsin(opt);
                  setRows(null);
                  const entry = manifest.asins.find((a) => a.asin === opt.value);
                  if (entry?.output_s3) {
                    setLoadingAsin(true);
                    fetchIgAsinResults(entry.output_s3)
                      .then((data) => setRows(data))
                      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
                      .finally(() => setLoadingAsin(false));
                  }
                }}
                options={manifest.asins
                  .filter((a) => a.status === 'succeeded')
                  .map((a) => {
                    // Perturbation manifests may omit actual_p50/p90 (e.g. a
                    // single-quantile run); only append the forecast summary
                    // when both are present. Capture in locals so TypeScript
                    // narrows them to `number` for the .toFixed() calls below.
                    const p50 = a.actual_p50;
                    const p90 = a.actual_p90;
                    const hasForecast =
                      typeof p50 === 'number' && typeof p90 === 'number';
                    return {
                      label: hasForecast
                        ? `${a.asin} (P50=${p50.toFixed(2)}, P90=${p90.toFixed(2)})`
                        : a.asin,
                      value: a.asin,
                    };
                  })}
                placeholder="Search or select an ASIN"
                filteringType="auto"
                filteringPlaceholder="Type ASIN to filter"
              />
            </FormField>
            {loadingAsin && <StatusIndicator type="loading">Loading ASIN results...</StatusIndicator>}
            {manifest.failed > 0 && (
              <Alert type="warning" header={`${manifest.failed} ASINs failed`}>
                {manifest.asins.filter((a) => a.status === 'failed').map((a) => `${a.asin}: ${a.error}`).join('; ')}
              </Alert>
            )}
          </SpaceBetween>
        </Container>
      )}

      {/* Perturbation job submitted info */}
      {methodActive && pertResponse && !rows && !loading && (
        <Alert type="success" header="Job Submitted">
          <SpaceBetween size="xs">
            <div><strong>Job ID:</strong> {pertResponse.jobId}</div>
            <div><strong>Output:</strong> {pertResponse.outputS3}</div>
            <div>Results are written as Parquet to S3. Use the job status endpoint to check progress.</div>
          </SpaceBetween>
        </Alert>
      )}

      {/* Results display */}
      {methodActive && rows && rows.length > 0 && (
        <Container
          header={
            <Header
              variant="h2"
              info={
                <Popover
                  header="Feature Attributions"
                  triggerType="text"
                  size="large"
                  content={<Box variant="p">{HOW_TO_READ_TEASER}</Box>}
                >
                  <Link variant="info">Info</Link>
                </Popover>
              }
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button iconName="download" onClick={downloadCsv}>
                    Download CSV
                  </Button>
                  <SegmentedControl
                    selectedId={display}
                    onChange={({ detail }) => setDisplay(detail.selectedId as Display)}
                    options={[
                      { id: 'chart', text: 'Chart' },
                      { id: 'table', text: 'Table' },
                    ]}
                  />
                </SpaceBetween>
              }
            >
              Feature Attributions — {selectedAsin?.value ?? asin} / FG {fg}
              {method === 'ig' ? ` / LT${leadtime}` : ''}
            </Header>
          }
        >
          <SpaceBetween size="l">
            <ExpandableSection headerText="How to read this" variant="footer">
              {method === 'ig' ? <HowToReadIg /> : <HowToReadPerturbation />}
            </ExpandableSection>
            {/* Perturbation multi-lead-time selector: only shown when the loaded
                perturbation result carries more than one distinct lead_time.
                Filters the table + bar chart below to the selected lead time.
                Never shown for IG (its CSVs have no lead_time column). */}
            {showLeadTimeSelector && (
              <FormField
                label="Lead time"
                description="Filter the attributions below to a single lead time"
              >
                <Select
                  selectedOption={selectedLeadTime}
                  onChange={({ detail }) =>
                    setSelectedLeadTime(detail.selectedOption as Option)
                  }
                  options={leadTimes.map((lt) => ({
                    label: `${lt} days`,
                    value: String(lt),
                  }))}
                  placeholder="Select a lead time"
                />
              </FormField>
            )}
            {summaryItems.length > 0 && (
              <Table
                variant="embedded"
                header={
                  <Header variant="h3" description="Forecast quantile values in units (not attribution scores). P50 = median (50th-percentile) forecast; P90 = 90th-percentile forecast.">
                    Forecast reconstruction
                  </Header>
                }
                columnDefinitions={[
                  { id: 'label', header: 'Row', cell: (i: SummaryItem) => i.label },
                  {
                    id: 'meaning',
                    header: 'Meaning',
                    cell: (i: SummaryItem) => i.meaning,
                  },
                  { id: 'p50', header: 'P50 (median)', cell: (i: SummaryItem) => i.p50 },
                  { id: 'p90', header: 'P90 (90th pct)', cell: (i: SummaryItem) => i.p90 },
                ]}
                items={summaryItems}
                sortingDisabled
              />
            )}
            {display === 'chart' ? (
              method === 'ig' ? (
                // IG has real per-day attribution, so the day-by-day heatmap applies.
                <SpaceBetween size="s">
                  <SegmentedControl
                    selectedId={heatmapMetric}
                    onChange={({ detail }) =>
                      setHeatmapMetric(detail.selectedId as 'p50_score' | 'p90_score')
                    }
                    label="Quantile"
                    options={[
                      { id: 'p50_score', text: 'P50' },
                      { id: 'p90_score', text: 'P90' },
                    ]}
                  />
                  <AttributionHeatmap rows={rows} fcd={fcd} metric={heatmapMetric} />
                </SpaceBetween>
              ) : (
                // Perturbation produces one score per feature (day_offset is
                // always 'N/A', no real per-day breakdown), so a per-feature
                // bar chart fits its data shape better than the day-based heatmap.
                <BarChart
                  series={[
                    {
                      title: 'P50 Total Attribution',
                      type: 'bar',
                      data: chartData,
                    },
                    {
                      title: 'P90 Total Attribution',
                      type: 'bar',
                      data: p90ChartData,
                    },
                  ]}
                  xDomain={chartData.map((d) => d.x)}
                  yTitle="Attribution (units contribution)"
                  xTitle="Feature"
                  horizontalBars
                  height={Math.max(300, featureTotals.length * 28)}
                  hideFilter
                  empty="No data"
                />
              )
            ) : (
              <Table
                columnDefinitions={[
                {
                  id: 'feature',
                  header: 'Feature',
                  cell: (item: IgAttributionRow) => item.feature,
                  sortingField: 'feature',
                },
                {
                  id: 'p50_rank',
                  header: 'P50 Rank',
                  cell: (item: IgAttributionRow) =>
                    item.p50_rank != null && item.p50_rank !== ''
                      ? String(item.p50_rank)
                      : '',
                  sortingField: 'p50_rank',
                },
                {
                  id: 'p90_rank',
                  header: 'P90 Rank',
                  cell: (item: IgAttributionRow) =>
                    item.p90_rank != null && item.p90_rank !== ''
                      ? String(item.p90_rank)
                      : '',
                  sortingField: 'p90_rank',
                },
                {
                  id: 'date',
                  header: 'Date',
                  cell: (item: IgAttributionRow) => item.date,
                  sortingField: 'date',
                },
                {
                  id: 'day_offset',
                  header: 'Day Offset',
                  cell: (item: IgAttributionRow) => String(item.day_offset),
                  sortingField: 'day_offset',
                },
                {
                  id: 'actual_value',
                  header: 'Actual',
                  cell: (item: IgAttributionRow) => item.actual_value !== '' ? String(item.actual_value) : '',
                  sortingField: 'actual_value',
                },
                {
                  id: 'baseline_value',
                  header: 'Baseline',
                  cell: (item: IgAttributionRow) => item.baseline_value !== '' ? String(item.baseline_value) : '',
                  sortingField: 'baseline_value',
                },
                {
                  id: 'p50_score',
                  header: 'P50 Score',
                  cell: (item: IgAttributionRow) => Number(item.p50_score).toFixed(4),
                  sortingField: 'p50_score',
                },
                {
                  id: 'p90_score',
                  header: 'P90 Score',
                  cell: (item: IgAttributionRow) => Number(item.p90_score).toFixed(4),
                  sortingField: 'p90_score',
                },
                {
                  id: 'p50_total',
                  header: 'P50 Total Attribution',
                  cell: (item: IgAttributionRow) => item.p50_total !== '' ? Number(item.p50_total).toFixed(4) : '',
                  sortingField: 'p50_total',
                },
                {
                  id: 'p90_total',
                  header: 'P90 Total Attribution',
                  cell: (item: IgAttributionRow) => item.p90_total !== '' ? Number(item.p90_total).toFixed(4) : '',
                  sortingField: 'p90_total',
                },
              ]}
              items={featureTableRows}
              sortingDisabled={false}
              variant="embedded"
              stripedRows
              stickyHeader
            />
          )}
          </SpaceBetween>
        </Container>
      )}

      {methodActive && rows && rows.length > 0 && (
        <AgentExplanationInline
          results={rows}
          metadata={{ asin: selectedAsin?.value ?? asin, fg, fcd, leadtime, span, business: business?.value || '', region: region?.value || '' }}
        />
      )}

      {methodActive && rows && rows.length > 0 && (
        <AgentChat
          results={rows}
          metadata={{ asin: selectedAsin?.value ?? asin, fg, fcd, leadtime, span, business: business?.value || '', region: region?.value || '' }}
        />
      )}

    </SpaceBetween>
  );
}
